import { isDestroying, isDestroyed } from '@ember/destroyable'
import type { QueryCache } from 'customer-flows/utils/core/query-cache'
import type { FetchDims, KeyedRange } from './keys'
import type { AvailabilityDatesFacade, EngineArgs } from './types'
import { createScopes, unionKeyed } from './scopes'
import { type EngineConfig, createCaches } from './caches'
import { createProjection } from './projection'
import { createSignals } from './signals'

export { canonicalizeQuantities } from './keys'
export type { AvailabilityDatesFacade, EngineArgs } from './types'

/**
 * A query store for availability. Entries are the source of truth: one verbatim BE response per
 * key, keyed per week for dates and per family for times, each carrying its own status and
 * freshness stamp. Reading `availabilityDates` returns the union projection of the current range
 * and drives stale-while-revalidate; there is no global clock, freshness is per entry.
 *
 * This module is the assembly and the public surface only. The parts, in dependency order:
 *
 * - `scopes.ts` — which keys, resolved from one `args()` snapshot (visible / anchored / lookAhead)
 * - `caches.ts` — the two `QueryCache` instances, their policy config, and fetch scheduling
 * - `projection.ts` — which entries feed which fold, and the two memoized derivations
 * - `signals.ts` — the five loading/verifying signals
 */
export function createAvailabilityEngine(ctx: object, args: () => EngineArgs, config: EngineConfig = {}) {
  // args() reads cross-store state via @context (lookupStore), which throws once the owner is
  // being torn down. Reads can land during/after teardown (a final render pass, a settled async),
  // so every read path resolves keys through this guard rather than calling args() directly.
  const alive = () => !isDestroying(ctx) && !isDestroyed(ctx)

  const scopes = createScopes(args, alive, { lookAhead: config.lookAhead })
  const caches = createCaches(ctx, config, { keyScopes: scopes.keyScopes, alive })
  const { currentValue, rangeCapacityDates } = createProjection(caches, scopes)
  const signals = createSignals(caches, scopes, args, alive)

  const { datesCache, timesCache } = caches

  // Readiness handle for the data the engine is actually fetching — the FETCH scope, not the
  // display scope (see `ensureAnchored` in caches.ts for why that distinction is load-bearing).
  function awaitCurrentRange(only?: 'dates' | 'times'): Promise<AvailabilityDate[]> {
    return Promise.all(caches.ensureAnchored({ only })).then(currentValue)
  }

  /**
   * Awaitable-shaped view onto the SCOPED projection: `.value` folds only the current range's
   * week keys plus their times overlays — i.e. the one question the UI is currently asking (this
   * activity/program, this visible date range, these ticket quantities). Reading `.value` is what
   * drives stale-while-revalidate for that scope: a stale or missing current-range entry schedules
   * a background revalidation. Contrast with `rangeCapacityDates()`, which folds the current
   * range's quantity-scoped AND seed entries together (rather than picking exclusively) and never
   * schedules a fetch.
   *
   * See `AvailabilityDatesFacade` for what `is`'s states mean.
   */
  const availabilityDates: AvailabilityDatesFacade = {
    get value() {
      return currentValue()
    },
    get error() {
      return caches.lastError
    },
    is: signals.is,
    promise: awaitCurrentRange,
    get unknownWeekRanges() {
      return signals.unknownWeekRanges()
    }
  }

  // Invalidates matching entries and returns a promise for the current range's revalidation.
  //
  // Soft (default) zeroes freshness so the next read serves stale data then revalidates; hard also
  // condemns the entry (unusable everywhere until a fresh response lands).
  //
  // `only` scopes invalidation to one family. Default is both, which is the right default and the
  // only thing production asks for: the two families answer one question between them, so
  // condemning a date's availability while leaving its times resident would serve a contradiction.
  //
  // This replaced a `predicate(key)` that had no production caller. Its only real use was telling
  // the two families apart by key prefix (`key.startsWith('times:')`) — which the cache split made
  // both structural and impossible to express by key, since the two maps no longer namespace their
  // keys. `only` says that directly; a per-key predicate would be generic surface with no consumer.
  //
  // In-scope matches are force-refetched so a refresh supersedes any in-flight fetch rather than
  // reusing it; out-of-scope matches just sit invalidated until a read reaches them. "In scope" is
  // the UNION of all three scopes (display + fetch + preload): forcing only the displayed keys left
  // anchored and look-ahead entries soft-invalidated but resident, so a hard condemn (after a failed
  // order) did not cover next-month preloaded data and condemned availability reappeared as soon as
  // the user paged forward.
  //
  // `invalidate()` with no args is soft + both families + in-scope force.
  function invalidate(options: { mode?: 'soft' | 'hard'; only?: 'dates' | 'times' } = {}): Promise<AvailabilityDate[]> {
    const { mode, only } = options
    const wants = (family: 'dates' | 'times') => !only || only === family

    if (wants('dates')) datesCache.invalidate({ mode })
    if (wants('times')) timesCache.invalidate({ mode })

    // An in-scope key with no entry yet has nothing to filter, so it force-fetches too. An
    // out-of-family key is left alone entirely — neither invalidated nor refetched.
    const { visible, anchored, lookAhead } = scopes.keyScopes()

    const forceIn = <T,>(cache: QueryCache<FetchDims, T>, keyed: KeyedRange[]) =>
      keyed.map(({ key, dims }) => cache.forceRevalidate(key, dims))

    const pending = [
      ...(wants('dates') ? forceIn(datesCache, unionKeyed(visible.dates, anchored.dates, lookAhead.dates)) : []),
      ...(wants('times') ? forceIn(timesCache, unionKeyed(visible.times, anchored.times)) : [])
    ]

    return Promise.all(pending).then(currentValue)
  }

  return {
    // The engine's whole derived-state surface. Every loading/verifying signal is a getter ON the
    // facade, so consumers re-read them as entries change; there are no duplicate top-level
    // getters mirroring them.
    availabilityDates,
    invalidate,
    rangeCapacityDates
  }
}

declare global {
  export type AvailabilityEngine = ReturnType<typeof createAvailabilityEngine>
}
